import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AlertTriangle, X } from "lucide-react";
import { api } from "@/lib/api";

interface Alert {
  type: "wildcard.expiring";
  apex: string;
  notAfter: string;
  daysToExpiry: number;
  severity: "medium" | "high";
}

const DISMISS_KEY = "openfront.alertBanner.dismissedUntil";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Sticky banner across the top of every authenticated page that polls
 * /alerts every 60s. Per-browser dismiss for 24h via localStorage; cert-
 * expiry severity escalates dismissal — when daysToExpiry hits 7 or less
 * the banner ignores prior dismissals (you really need to act now).
 */
export function AlertBanner() {
  const q = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api.get<{ alerts: Alert[] }>("/alerts").then((r) => r.alerts),
    refetchInterval: 60_000,
    // 404s on logged-out pages — fail silently.
    retry: false,
  });

  const [dismissedUntil, setDismissedUntil] = useState<number>(() => {
    const v = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return Number.isFinite(v) ? v : 0;
  });

  useEffect(() => {
    const onStorage = () => setDismissedUntil(Number(localStorage.getItem(DISMISS_KEY) ?? 0));
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const alerts = q.data ?? [];
  if (alerts.length === 0) return null;

  const hasHigh = alerts.some((a) => a.severity === "high");
  const dismissed = !hasHigh && dismissedUntil > Date.now();
  if (dismissed) return null;

  const a = alerts[0]!;
  return (
    <div className={`w-full border-b px-4 py-2 flex items-center gap-3 text-sm ${
      hasHigh
        ? "bg-danger-subtle border-danger/40 text-foreground"
        : "bg-attention-subtle border-attention/40 text-foreground"
    }`}>
      <AlertTriangle className={`h-4 w-4 shrink-0 ${hasHigh ? "text-danger" : "text-attention"}`} />
      <div className="flex-1 min-w-0">
        Wildcard cert for <span className="font-mono font-semibold">{a.apex}</span> expires in
        {" "}<span className="font-mono">{a.daysToExpiry}</span> day{a.daysToExpiry === 1 ? "" : "s"}.
        {alerts.length > 1 && <span className="text-muted-foreground ml-1">(+{alerts.length - 1} more)</span>}
      </div>
      <Link
        to="/admin"
        className="text-xs font-semibold underline hover:no-underline shrink-0"
      >
        Renew now
      </Link>
      {!hasHigh && (
        <button
          onClick={() => {
            const until = Date.now() + DISMISS_TTL_MS;
            localStorage.setItem(DISMISS_KEY, String(until));
            setDismissedUntil(until);
          }}
          aria-label="Dismiss for 24 hours"
          className="text-muted-foreground hover:text-foreground shrink-0"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
